import type { CollectResult, ServerReport, VisitorIdentity } from '@fraudeai/schema';

/**
 * Обращения к серверной части.
 *
 * Запрос идёт напрямую на адрес API, без прокси разработчика. Это не мелочь:
 * любой посредник переписывает заголовки в своём порядке, а порядок заголовков —
 * один из проверяемых сигналов. Прокси уничтожил бы ровно то, что мы измеряем.
 *
 * В боевой конфигурации API обязан жить на том же origin, что и страница, —
 * его отдаёт тот же сервер, который снимает отпечаток TLS.
 */

export const API_BASE: string =
  (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://127.0.0.1:8787';

export class ApiUnavailableError extends Error {
  constructor(cause: string) {
    super(cause);
    this.name = 'ApiUnavailableError';
  }
}

async function readError(response: Response): Promise<string> {
  // Отказ по частоте нельзя показывать как поломку сервера: причина
  // совсем другая, и посетителю есть что с ней сделать — подождать.
  // Особенно это важно за общим адресом (мобильный оператор, офис,
  // туннель), где соседи расходуют тот же лимит.
  if (response.status === 429) {
    return 'с вашего адреса пришло слишком много проверок подряд — подождите полминуты';
  }
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    // тело не в JSON — ниже вернём статус
  }
  return `сервер ответил ${response.status}`;
}

/**
 * Метка прогона, заданная в адресе: `?label=real` или `?label=antidetect`.
 *
 * Нужна, чтобы отличать наши ошибки от верных срабатываний. Без неё разбор
 * упирается в «мне кажется, тут ложное срабатывание»: по самому отчёту не
 * видно, сидел ли за браузером живой человек или антидетект-сборка, а от
 * этого зависит, чинить правило или радоваться ему.
 *
 * Принимаются только два значения. Всё прочее отбрасывается: метка попадает
 * в хранилище и в счёт качества, и мусор в ней хуже её отсутствия.
 */
function runLabel(): string | undefined {
  try {
    const value = new URLSearchParams(location.search).get('label');
    return value === 'real' || value === 'antidetect' ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Какой именно сборкой сделан прогон: `?product=octo`, `sphere`, `dolphin`.
 *
 * Нужно, чтобы видеть не только сколько подделок проходит, но и какие
 * именно. Три сборки ведут себя по-разному, и общее число это скрывает: одна
 * может ловиться всегда, другая никогда, а в сумме выйдет «половина».
 *
 * Значение приводится к короткому виду и обрезается: оно попадает в
 * хранилище, и мусор в нём не нужен.
 */
function runProduct(): string | undefined {
  try {
    const raw = new URLSearchParams(location.search).get('product');
    if (!raw) return undefined;
    const value = raw.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 24);
    return value || undefined;
  } catch {
    return undefined;
  }
}

/** Отправляет измерения и получает авторитетный отчёт. */
export async function submitReport(
  result: CollectResult,
  identity: VisitorIdentity,
): Promise<ServerReport> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/v1/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Токен пробы едет вместе с отчётом: по нему сервер подтягивает
      // наблюдения STUN, HTTP/3 и DNS, снятые другими процессами.
      body: JSON.stringify({
        ...result,
        identity,
        probeToken: result.signals.probes?.token ?? undefined,
        label: runLabel(),
        product: runProduct(),
      }),
    });
  } catch (cause) {
    throw new ApiUnavailableError(cause instanceof Error ? cause.message : String(cause));
  }

  if (!response.ok) throw new Error(await readError(response));
  return (await response.json()) as ServerReport;
}

/** Загружает сохранённый отчёт по ссылке. */
export async function fetchReport(id: string): Promise<ServerReport> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/v1/reports/${encodeURIComponent(id)}`);
  } catch (cause) {
    throw new ApiUnavailableError(cause instanceof Error ? cause.message : String(cause));
  }

  if (!response.ok) throw new Error(await readError(response));
  return (await response.json()) as ServerReport;
}

/** Ссылка для шаринга отчёта. */
export function shareUrl(id: string): string {
  const url = new URL(location.href);
  url.search = `?report=${encodeURIComponent(id)}`;
  url.hash = '';
  return url.toString();
}
